/**
 * Canvas image-sequence scrubber (Apple-style product pages).
 *
 * Scroll progress maps to a frame index; frames are individual webp files
 * fetched once as Blobs (cheap RAM), decoded to ImageBitmap on demand through
 * a small LRU window (bounded GPU memory), and blitted to a canvas. No
 * <video> seek pipeline anywhere in the scroll path.
 */

export interface SequenceManifest {
  frames: number;
  width: number;
  height: number;
}

const DECODE_CACHE = 24; // max live ImageBitmaps per sequence
const FETCH_CONCURRENCY = 8;

export class SequenceScrubber {
  readonly canvas: HTMLCanvasElement;
  private readonly ctx2d: CanvasRenderingContext2D;
  private manifest: SequenceManifest | null = null;
  private blobs: Array<Blob | null> = [];
  private bitmaps = new Map<number, ImageBitmap>();
  private bitmapOrder: number[] = [];
  private decoding = new Map<number, Promise<ImageBitmap | null>>();
  private drawnIndex = -1;
  private targetIndex = 0;
  private rafId = 0;
  private disposed = false;

  constructor(
    private readonly baseUrl: string,
    container: HTMLElement,
    /** Cache-bust query (e.g. "?v=12") appended to every request. */
    private readonly cacheBust = '',
  ) {
    this.canvas = document.createElement('canvas');
    this.canvas.className = 'scene-canvas';
    this.canvas.setAttribute('aria-hidden', 'true');
    const ctx2d = this.canvas.getContext('2d');
    if (!ctx2d) throw new Error('2d context unavailable');
    this.ctx2d = ctx2d;
    container.appendChild(this.canvas);
  }

  get frameCount(): number {
    return this.manifest?.frames ?? 0;
  }

  /** Fraction of frames fetched (for loading UIs). */
  get loadedRatio(): number {
    if (!this.manifest) return 0;
    const loaded = this.blobs.filter(Boolean).length;
    return loaded / this.manifest.frames;
  }

  private frameUrl(i: number): string {
    const n = String(i + 1).padStart(4, '0');
    return `${this.baseUrl}/f_${n}.webp${this.cacheBust}`;
  }

  /**
   * Fetch manifest + all frame blobs (bounded concurrency).
   * Resolves when every frame is fetched; rejects only if the manifest or
   * more than 10% of frames fail (partial sequences still scrub — drawing
   * falls back to the nearest fetched frame).
   */
  async prefetch(onProgress?: (ratio: number) => void): Promise<void> {
    const mRes = await fetch(`${this.baseUrl}/seq.json${this.cacheBust}`);
    if (!mRes.ok) throw new Error(`seq manifest ${mRes.status}`);
    this.manifest = (await mRes.json()) as SequenceManifest;
    const total = this.manifest.frames;
    this.canvas.width = this.manifest.width;
    this.canvas.height = this.manifest.height;
    this.blobs = new Array<Blob | null>(total).fill(null);

    // Coarse-to-fine fetch order (every 8th frame, then 4th, 2nd, rest): a
    // partially-loaded sequence scrubs at reduced frame rate across the WHOLE
    // shot instead of playing fully then skipping the un-fetched tail.
    const order: number[] = [];
    for (const stride of [8, 4, 2, 1]) {
      for (let i = 0; i < total; i += stride) {
        if (stride === 8 ? i % 8 === 0 : i % (stride * 2) !== 0) order.push(i);
      }
    }
    let next = 0;
    let done = 0;
    let failed = 0;
    const worker = async (): Promise<void> => {
      while (next < order.length && !this.disposed) {
        const i = order[next++] as number;
        try {
          const res = await fetch(this.frameUrl(i));
          if (!res.ok) throw new Error(String(res.status));
          this.blobs[i] = await res.blob();
        } catch {
          failed += 1;
        }
        done += 1;
        onProgress?.(done / total);
      }
    };
    await Promise.all(
      Array.from({ length: FETCH_CONCURRENCY }, () => worker()),
    );
    if (failed > total * 0.1) {
      throw new Error(`sequence fetch failed for ${failed}/${total} frames`);
    }
    // First paint so the canvas is never blank once ready, then pre-decode a
    // spread across the shot — arrival scrubs hit warm bitmaps instead of
    // paying first-decode latency (the visible "beat" on scene entry).
    this.setProgress(0);
    for (let d = 0; d < 8; d++) {
      const idx = Math.round((d / 7) * (total - 1));
      void this.bitmapFor(idx);
    }
  }

  /** Map progress 0..1 to a frame and draw it (rAF-coalesced). */
  setProgress(progress: number): void {
    if (!this.manifest) return;
    const clamped = Math.min(1, Math.max(0, progress));
    this.targetIndex = Math.round(clamped * (this.manifest.frames - 1));
    if (this.rafId) return;
    this.rafId = requestAnimationFrame(() => {
      this.rafId = 0;
      void this.drawTarget();
    });
  }

  private async drawTarget(): Promise<void> {
    if (this.disposed || !this.manifest) return;
    const want = this.targetIndex;
    const bitmap = await this.bitmapFor(want);
    if (this.disposed) return;
    // Scroll may have moved on while decoding; only draw if still relevant
    // (or nothing has been drawn yet).
    if (bitmap && (want === this.targetIndex || this.drawnIndex === -1)) {
      this.ctx2d.drawImage(bitmap, 0, 0);
      this.drawnIndex = want;
    }
    // Warm neighbours in scroll direction for the next tick.
    const dir = want >= this.drawnIndex ? 1 : -1;
    for (let d = 1; d <= 3; d++) {
      const n = want + dir * d;
      if (n >= 0 && n < this.manifest.frames) void this.bitmapFor(n);
    }
  }

  /** Decode (or reuse) the bitmap for frame i; nearest fetched frame wins. */
  private async bitmapFor(i: number): Promise<ImageBitmap | null> {
    const idx = this.nearestFetched(i);
    if (idx === -1) return null;
    const cached = this.bitmaps.get(idx);
    if (cached) return cached;
    const inflight = this.decoding.get(idx);
    if (inflight) return inflight;
    const blob = this.blobs[idx];
    if (!blob) return null;
    const p = createImageBitmap(blob)
      .then((bmp) => {
        this.decoding.delete(idx);
        if (this.disposed) {
          bmp.close();
          return null;
        }
        this.bitmaps.set(idx, bmp);
        this.bitmapOrder.push(idx);
        while (this.bitmapOrder.length > DECODE_CACHE) {
          const evict = this.bitmapOrder.shift();
          if (evict !== undefined && evict !== idx) {
            this.bitmaps.get(evict)?.close();
            this.bitmaps.delete(evict);
          }
        }
        return bmp;
      })
      .catch(() => {
        this.decoding.delete(idx);
        return null;
      });
    this.decoding.set(idx, p);
    return p;
  }

  private nearestFetched(i: number): number {
    if (this.blobs[i]) return i;
    for (let d = 1; d < this.blobs.length; d++) {
      if (this.blobs[i - d]) return i - d;
      if (this.blobs[i + d]) return i + d;
    }
    return -1;
  }

  dispose(): void {
    this.disposed = true;
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.bitmaps.forEach((b) => b.close());
    this.bitmaps.clear();
    this.bitmapOrder = [];
    this.blobs = [];
    this.canvas.remove();
  }
}
